import React, { useState } from 'react';
import { auth } from '../firebase';
import { signInWithEmailAndPassword, sendPasswordResetEmail, getMultiFactorResolver, TotpMultiFactorGenerator } from 'firebase/auth';
import { cleanCode, twoStepError } from '../data/twoStep';   // ruling 303 (D4): the second step at sign-in
// RULING 184 (2026-09-18): the password rules, generated from data\YCP_AUTH_POLICY.json - the same file
// the live policy is applied from. Never typed into this page a second time (directives XI).
import { rulesSentence } from '../data/authPolicy';
import { PLATFORM_NAME } from '../installation';   // section 768: the installation's own name (ruling 312)

// Record 612 (Bob 10/8; ruling 286 Q2): the hint shown after a failed sign-in when a reset was sent IN THIS SESSION.
// Moved out of the wrong-password branch so the generic lockout / disabled sentence carries the same words.
const RESET_HINT = ' You reset your password a moment ago, so the account itself is fine. The usual cause' +
  ' is the browser filling in the OLD saved password. Clear the box completely, type the' +
  ' new one by hand, and if it still fails try a private window - nothing is saved there' +
  ' to fill in.';
const resetHintFor = (resetSent) => (resetSent ? RESET_HINT : '');

export default function Login({ onApply }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  // RULING 303 (D4): an account with authenticator-app two-step sign-in turned on is asked for its code here.
  const [mfa, setMfa] = useState(null);        // { resolver, hint } while the second step is pending
  const [mfaCode, setMfaCode] = useState('');

  const handleSecondStep = async () => {
    const c = cleanCode(mfaCode);
    if (!c) { setError(twoStepError('auth/missing-code')); return; }
    setLoading(true);
    setError('');
    try {
      await mfa.resolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(mfa.hint.uid, c));
    } catch (e) {
      setError(twoStepError(e && e.code));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!email || !password) return;
    setLoading(true);
    setError('');
    // 2026-09-14 (task list section 218): the page used to say "Invalid email or password" for EVERY
    // failure, including a lockout and a typo in the address. Say what actually happened.
    const addr = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(addr)) {
      // Ruling 286.1 (Record 583, audit finding): the page never echoes the address back - not on a format error either.
      setError('That email address is incomplete. It needs a domain, like gmail.com.');
      setLoading(false);
      return;
    }
    try {
      await signInWithEmailAndPassword(auth, addr, password);
    } catch (e) {
      const code = (e && e.code) || '';
      // RULING 303 (D4): the password was right and the account has two-step sign-in on - ask for the app's code.
      if (code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, e);
        const hint = resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
        if (hint) { setMfa({ resolver, hint }); setMfaCode(''); return; }
        setError('This account uses a kind of two-step sign-in this page does not offer. Contact your General Chairperson.');
        return;
      }
      // Record 612 (Bob 10/8; ruling 286 Q2, agreed): THE LOCKOUT AND THE DISABLED ACCOUNT NOW SAY NOTHING ABOUT THE
      // ACCOUNT. "Temporarily locked" and "disabled" each told whoever typed an address that an account exists behind
      // it, and what state it is in. Both read one generic sentence. The reset-in-this-session hint is kept (Bob: it
      // reveals nothing - it is shown only to whoever asked for a reset a moment ago, and says only what that reset said).
      if (code === 'auth/too-many-requests' || code === 'auth/user-disabled') {
        setError("We couldn't sign you in. If this keeps happening, contact your General Chairperson." + resetHintFor(resetSent));
      } else if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
        // RULING 184: say the one thing that would have saved Bob two resets on 2026-09-18. A password
        // shorter than the minimum cannot be one set or changed recently - the policy refuses them - so
        // a member who has reset and is still typing his OLD password is told so, in plain words.
        // Only on FAILURE: an older, shorter password that still works signs in and never sees this.
        // 2026-09-25: if a reset was sent in THIS session and the sign-in still fails, repeating
        // "wrong email or password" tells the reader nothing he has not already rejected. Name the
        // likely cause, because it is nearly always the same one and he cannot see it.
        // Ruling 286 (Greg's 10/1 note H1): no length, no address echoed back.
        // Record 612: the hint after a reset is RESET_HINT, shared with the generic sentence above - one sentence, one place.
        setError('Wrong email or password.' + resetHintFor(resetSent));
      } else if (code === 'auth/network-request-failed') {
        setError('No connection to the sign-in service. Check the network and try again.');
      } else {
        setError('Sign-in failed: ' + (code || (e && e.message) || 'unknown error') + '.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!email) { setError('Enter your email address above first.'); return; }
    setLoading(true);
    setError('');
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
      setResetMode(false);
      // 2026-09-25. EMPTY THE BOX. It was coming back from the reset still holding the password the
      // browser had saved BEFORE the reset, and that ghost is what got submitted. The account was
      // fine, the new password was live, and the page said "wrong email or password".
      setPassword('');
    } catch {
      setError('Could not send reset email. Check the address and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-box">
        <div className="login-logo">
          {/* RULING 185 (2026-09-19): SMART-TD comes off the platform's screens; it heads the documents instead (ruling 188). */}
          <h1 style={{ margin: 0, fontSize: 22 }}>{PLATFORM_NAME}</h1>
          <p>Labor Agreement Management and Claims Adjudication</p>
          {/* RULING 274 (Bob, 2026-10-03): his own statement, under the title. Text is his, word for word. */}
          <p className="pitch-line" style={{ fontStyle: 'italic', fontSize: 13, lineHeight: 1.6, color: '#C9D6E3', marginTop: 10 }}>
            {"The corporate dimension prevents workers from making and implementing common sense procedures and protocols. The handful of lives that are adversely affected are not as important as the stockholders and the bottom line."}
          </p>
        </div>
        {error && <div className="alert-banner">&#9888; {error}</div>}
        {resetSent && (
          <div style={{ background: '#0A2A1A', border: '1px solid #27AE60', borderRadius: 6, padding: '10px 14px', marginBottom: 14, color: '#40C070', fontSize: 13 }}>
            Password reset email sent. Check your inbox and spam folder.
            <div style={{ marginTop: 6 }}>
              The password box below has been emptied on purpose. <strong>Type the new password in by
              hand</strong> when you come back — if your browser fills it in for you, that is the OLD
              one and it will not work.
            </div>
            {/* RULING 184: the rules BEFORE he reaches the reset page, so the first password he picks is accepted. */}
            <div style={{ marginTop: 6 }}>When you choose the new one: {rulesSentence()} Write it down before you save it.</div>
          </div>
        )}
        <div className="form-group">
          <label className="form-label">Email Address</label>
          <input aria-label="Email Address"
            className="form-input"
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !resetMode && handleLogin()}
          />
        </div>
        {mfa ? (
          <div>
            <div className="form-group">
              <label className="form-label">Code from your authenticator app</label>
              <input aria-label="Code from your authenticator app"
                className="form-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="6-digit code"
                value={mfaCode}
                onChange={e => setMfaCode(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !loading && handleSecondStep()}
              />
            </div>
            <button
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', padding: '12px', opacity: loading ? 0.6 : 1, marginBottom: 12 }}
              onClick={handleSecondStep}
              disabled={loading}
            >
              {loading ? 'Checking...' : 'Continue'}
            </button>
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={() => { setMfa(null); setMfaCode(''); setError(''); setPassword(''); }}
                style={{ background: 'none', border: 'none', color: '#8AA4BF', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
              >
                Back to Sign In
              </button>
            </div>
          </div>
        ) : null}
        {!mfa && !resetMode && (
          <div className="form-group">
            <label className="form-label">Password</label>
            <input aria-label="Password"
              /* The key changes once a reset has been sent, which REMOUNTS the field. Without that the
                 browser goes on treating it as the same box and refills the saved password; with it,
                 plus autoComplete="new-password", it offers to save the new one instead. */
              key={resetSent ? 'pw-after-reset' : 'pw'}
              className="form-input"
              type="password"
              autoComplete={resetSent ? 'new-password' : 'current-password'}
              placeholder={resetSent ? 'Type your NEW password' : 'Enter password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
            />
          </div>
        )}
        {mfa ? null : !resetMode ? (
          <div>
            <button
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', padding: '12px', opacity: loading ? 0.6 : 1, marginBottom: 12 }}
              onClick={handleLogin}
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={() => { setResetMode(true); setError(''); }}
                style={{ background: 'none', border: 'none', color: '#8AA4BF', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
              >
                Forgot Password?
              </button>
            </div>
            {/* ONB-1: the only way onto this platform that does not require already being on it.
                Ruling 193.6 is said here, not after he has filled the form in: applying is not
                joining, and the man who decides is his Local Chairperson, not this screen. */}
            {onApply && (
              <div style={{ textAlign: 'center', marginTop: 14 }}>
                <div style={{ fontSize: 12, color: '#6F8AA6', marginBottom: 6 }}>No account yet?</div>
                <button
                  onClick={onApply}
                  style={{ background: 'none', border: '1px solid #2B5C8A', borderRadius: 6, color: '#8AA4BF', fontSize: 12, cursor: 'pointer', padding: '7px 14px' }}
                >
                  Apply for access
                </button>
              </div>
            )}
          </div>
        ) : (
          <div>
            <button
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', padding: '12px', opacity: loading ? 0.6 : 1, marginBottom: 12 }}
              onClick={handleReset}
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Password Reset Email'}
            </button>
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={() => { setResetMode(false); setError(''); }}
                style={{ background: 'none', border: 'none', color: '#8AA4BF', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
              >
                Back to Sign In
              </button>
            </div>
          </div>
        )}
        <hr className="login-divider" />
        <div className="login-footer">
          Version 1.0 | {PLATFORM_NAME} | 2026
        </div>
      </div>
    </div>
  );
}
